import { create } from 'zustand';
import { User } from '@/types';
import { signIn, signUp, signOut, onAuthStateChange } from '@/lib/supabase';
import type { User as SupabaseUser, AuthError } from '@supabase/supabase-js';
import { identifyUser, resetPostHog, track } from '@/lib/posthog';

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isInitialized: boolean;
  isLoggingOut: boolean;
  isSuperadmin: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, fullName?: string) => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: User | null) => void;
  initialize: () => Promise<void>;
  updateProfile: (updates: { name?: string }) => Promise<void>;
}

// Helper to convert Supabase user to our User type
const convertSupabaseUser = (supabaseUser: SupabaseUser): User => ({
  id: supabaseUser.id,
  email: supabaseUser.email || '',
  name: supabaseUser.user_metadata?.full_name ||
        supabaseUser.user_metadata?.name ||
        supabaseUser.email?.split('@')[0] ||
        'User',
  created_at: supabaseUser.created_at,
});

// Check if user is a superadmin via app_metadata
const checkIsSuperadmin = (supabaseUser: SupabaseUser): boolean => {
  return supabaseUser.app_metadata?.is_superadmin === true;
};

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isLoading: false,
  isAuthenticated: false,
  isInitialized: false,
  isLoggingOut: false,
  isSuperadmin: false,
  
  login: async (email: string, password: string) => {
    set({ isLoading: true });
    try {
      const { user: supabaseUser } = await signIn(email, password);
      
      if (supabaseUser) {
        const user = convertSupabaseUser(supabaseUser);
        const isSuperadmin = checkIsSuperadmin(supabaseUser);
        set({
          user,
          isAuthenticated: true,
          isLoading: false,
          isSuperadmin
        });
      } else {
        throw new Error('Login failed');
      }
    } catch (error) {
      set({ isLoading: false });
      const authError = error as AuthError;
      throw new Error(authError.message || 'Login failed');
    }
  },

  signup: async (email: string, password: string, fullName?: string) => {
    set({ isLoading: true });
    try {
      const { user: supabaseUser, session } = await signUp(email, password, fullName);
      
      if (supabaseUser && session) {
        // User is created and logged in
        const user = convertSupabaseUser(supabaseUser);
        set({
          user,
          isAuthenticated: true,
          isLoading: false,
          isInitialized: true
        });
        track('signup_completed', { email: user.email });
      } else if (supabaseUser && !session) {
        // User created but needs email confirmation
        set({ isLoading: false });
        throw new Error('Please check your email to confirm your account before signing in');
      } else {
        // Something went wrong
        set({ isLoading: false });
        throw new Error('Failed to create account');
      }
    } catch (error) {
      set({ isLoading: false });
      const authError = error as AuthError;
      throw new Error(authError.message || 'Signup failed');
    }
  },
  
  logout: async () => {
    // Set logging out flag to prevent ProtectedRoute from redirecting to /login
    set({ isLoggingOut: true });

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://ingesta.ai';

    try {
      await signOut();
    } catch (error) {
      console.error('Logout error:', error);
    }

    // Clear workspace data from localStorage to prevent stale data on next login
    try {
      localStorage.removeItem('ingesta-current-workspace');
      localStorage.removeItem('ingesta-recent-workspaces');
      localStorage.removeItem('ingesta-workspace-user-id');
    } catch (error) {
      console.warn('Failed to clear workspace data:', error);
    }

    // Detach the PostHog distinct id so the next anonymous visitor on
    // this device isn't tagged as the previous user.
    resetPostHog();

    // Redirect to marketing site after signOut
    globalThis.location.href = siteUrl;
  },
  
  setUser: (user) => {
    set({
      user,
      isAuthenticated: !!user
    });
    // Tag this PostHog session with the user's email so events going
    // forward attach to a Person profile. No-op on logout (user=null);
    // resetPostHog is invoked in the logout flow itself.
    if (user?.email) {
      identifyUser(user.email, {
        email: user.email,
        name: user.name,
      });
    }
  },

  updateProfile: async (updates: { name?: string }) => {
    const { supabase } = await import('@/lib/supabase');

    const { data, error } = await supabase.auth.updateUser({
      data: {
        full_name: updates.name,
        name: updates.name,
      }
    });

    if (error) {
      throw new Error(error.message);
    }

    if (data.user) {
      const user = convertSupabaseUser(data.user);
      set({ user });
    }
  },

  initialize: async () => {
    if (get().isInitialized) {
      return;
    }

    set({ isLoading: true });

    try {
      // Get current session (checks local storage first)
      const { getCurrentSession } = await import('@/lib/supabase');
      const session = await getCurrentSession();

      if (session?.user) {
        const user = convertSupabaseUser(session.user);
        const isSuperadmin = checkIsSuperadmin(session.user);
        set({
          user,
          isAuthenticated: true,
          isLoading: false,
          isInitialized: true,
          isSuperadmin
        });
      } else {
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          isInitialized: true,
          isSuperadmin: false
        });
      }
    } catch (error) {
      console.error('Auth initialization error:', error);
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        isInitialized: true
      });
    }

    // Set up auth state listener
    onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        const currentUserId = get().user?.id;
        const newUserId = session.user.id;
        const user = convertSupabaseUser(session.user);
        const isSuperadmin = checkIsSuperadmin(session.user);

        // If switching users (not first login), clear other stores
        if (currentUserId && currentUserId !== newUserId) {
          console.log('User switched, clearing related stores');
          // Dynamically import to avoid circular dependencies
          const { useWorkspaceStore } = await import('./workspace');
          const { useSubscriptionStore } = await import('./subscription');
          useWorkspaceStore.getState().clearWorkspace();
          useSubscriptionStore.getState().clearSubscription();
        }

        set({
          user,
          isAuthenticated: true,
          isSuperadmin
        });

        // Accept any pending workspace invitations (fire-and-forget)
        import('@/lib/database').then(({ acceptPendingInvites }) => {
          acceptPendingInvites().then((count) => {
            if (count > 0) {
              console.log(`Accepted ${count} pending workspace invitation(s)`);
            }
          });
        });
      } else if (event === 'SIGNED_OUT') {
        set({
          user: null,
          isAuthenticated: false,
          isSuperadmin: false
        });
      } else if (event === 'TOKEN_REFRESHED' && session?.user) {
        // Also update on token refresh in case metadata changed
        const user = convertSupabaseUser(session.user);
        const isSuperadmin = checkIsSuperadmin(session.user);
        set({
          user,
          isAuthenticated: true,
          isSuperadmin
        });
      } else if (event === 'USER_UPDATED' && session?.user) {
        // Keep the store in sync when supabase.auth.updateUser is called
        // anywhere (e.g., onboarding sets full_name). Without this, callers
        // must manually push the new user into the store.
        const user = convertSupabaseUser(session.user);
        const isSuperadmin = checkIsSuperadmin(session.user);
        set({
          user,
          isAuthenticated: true,
          isSuperadmin
        });
      }
    });
  },
}));